"use client";

import DynamicComponent from "@/components/DynamicComponentLoader";
import { FloatingWhatsAppButton } from "@/components/FloatingWhatsAppButton";
import { useConfig } from "@/context/ConfigContext";
import { useBanners } from "@/hooks/useBanners";
import { useCollections } from "@/hooks/useCollections";
import { useProductCategories } from "@/hooks/useProductCategories";
import { useProducts } from "@/hooks/useProducts";
import { getSortedComponents } from "@/utils/componentMapper";
import {
  buildProductImageUrl,
  formatRupiah,
  getProductCategory,
  getProductDetailHref,
  getProductDiscountPrice,
  getProductPrice,
  getProductRating,
  isProductPreorder,
} from "@/utils/dataHelpers";
import { Tab } from "@headlessui/react";
import { RiArrowRightSLine } from "@react-icons/all-files/ri/RiArrowRightSLine";
import { RiEyeLine } from "@react-icons/all-files/ri/RiEyeLine";
import { RiHeart3Line } from "@react-icons/all-files/ri/RiHeart3Line";
import { RiShoppingBag3Line } from "@react-icons/all-files/ri/RiShoppingBag3Line";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";

// Dynamic imports untuk fallback Header dan Footer
const Header = dynamic(
  () => import("@/components/Header").then((mod) => mod.Header),
  {
    ssr: false,
  }
);
const Footer = dynamic(
  () => import("@/components/Footer").then((mod) => mod.Footer),
  {
    ssr: false,
  }
);

/**
 * SafeImage - Wrapper Next.js Image dengan fallback berbasis React state.
 * Mencegah infinite loop yang terjadi saat onError mutasi DOM langsung.
 */
function SafeImage({ src, fallback, onError: _onError, ...props }) {
  const [imgSrc, setImgSrc] = useState(src);

  // Reset src jika prop berubah (misal data reload)
  useEffect(() => {
    setImgSrc(src);
  }, [src]);

  return <Image {...props} src={imgSrc} onError={() => setImgSrc(fallback)} />;
}

/**
 * DynamicComponentRenderer - Utility component untuk render header dan footer secara dynamic
 *
 * @param {Object} props
 * @param {boolean} props.renderHeader - Apakah render header (default: true)
 * @param {boolean} props.renderFooter - Apakah render footer (default: true)
 * @param {Object} props.headerProps - Props tambahan untuk header component
 * @param {Object} props.footerProps - Props tambahan untuk footer component
 * @param {string} props.imageBaseUrl - Base URL untuk images
 * @param {Array} props.categories - Categories data (optional, akan fetch jika tidak ada)
 * @param {boolean} props.categoriesLoading - Loading state untuk categories
 * @param {string} props.categoriesError - Error state untuk categories
 * @returns {JSX.Element|null}
 */
export default function DynamicComponentRenderer({
  renderHeader = true,
  renderFooter = true,
  headerProps = {},
  footerProps = {},
  imageBaseUrl = "",
  categories: providedCategories = null,
  categoriesLoading: providedCategoriesLoading = false,
  categoriesError: providedCategoriesError = null,
}) {
  const { configData } = useConfig();

  // Fetch categories jika tidak disediakan
  const {
    categories: fetchedCategories,
    loading: fetchedCategoriesLoading,
    error: fetchedCategoriesError,
  } = useProductCategories();

  // Gunakan provided categories atau fetched categories
  const categories = providedCategories ?? fetchedCategories;
  const categoriesLoading =
    providedCategoriesLoading || fetchedCategoriesLoading;
  const categoriesError = providedCategoriesError || fetchedCategoriesError;

  // Get sorted components dari config
  const sortedComponents = useMemo(() => {
    if (!configData?.ui_components) return null;
    return getSortedComponents(configData.ui_components);
  }, [configData]);

  // Get header dan footer components
  const headerComponent = useMemo(() => {
    if (!sortedComponents) return null;
    return sortedComponents.find((c) => c.component_type === "header");
  }, [sortedComponents]);

  const footerComponent = useMemo(() => {
    if (!sortedComponents) return null;
    return sortedComponents.find((c) => c.component_type === "footer");
  }, [sortedComponents]);

  // Get imageBaseUrl dari config jika tidak disediakan
  const finalImageBaseUrl =
    imageBaseUrl || configData?.config?.api?.imageUrl || "";

  // Render header (dynamic atau fallback)
  const renderHeaderComponent = () => {
    if (!renderHeader) return null;

    if (headerComponent) {
      return (
        <DynamicComponent
          componentConfig={headerComponent}
          imageBaseUrl={finalImageBaseUrl}
          categories={categories}
          loading={categoriesLoading}
          {...headerProps}
        />
      );
    }
    // Fallback ke Header default
    return (
      <Header
        categories={categories}
        loading={categoriesLoading}
        {...headerProps}
      />
    );
  };

  // Render footer (dynamic atau fallback)
  const renderFooterComponent = () => {
    if (!renderFooter) return null;

    if (footerComponent) {
      return (
        <>
          <DynamicComponent
            componentConfig={footerComponent}
            imageBaseUrl={finalImageBaseUrl}
            {...footerProps}
          />
          <FloatingWhatsAppButton />
        </>
      );
    }
    // Fallback ke Footer default
    return <Footer {...footerProps} />;
  };

  // Jika hanya render header atau footer saja, return component yang sesuai
  if (renderHeader && !renderFooter) {
    return renderHeaderComponent();
  }
  if (!renderHeader && renderFooter) {
    return renderFooterComponent();
  }

  // Default: render keduanya
  return (
    <>
      {renderHeaderComponent()}
      {renderFooterComponent()}
    </>
  );
}

// Export individual components untuk fleksibilitas lebih
export function DynamicHeader(props) {
  return (
    <DynamicComponentRenderer
      renderHeader={true}
      renderFooter={false}
      {...props}
    />
  );
}

export function DynamicFooter(props) {
  return (
    <DynamicComponentRenderer
      renderHeader={false}
      renderFooter={true}
      {...props}
    />
  );
}

// Helper function untuk mendapatkan banner image URL
const getBannerImageUrl = (banner, imageBaseUrl) => {
  if (!banner?.photo) return "/images/banner/banner-first.webp";

  // Jika sudah full URL
  if (
    banner.photo.startsWith("http://") ||
    banner.photo.startsWith("https://")
  ) {
    return banner.photo;
  }

  // Jika path relatif dari API
  if (imageBaseUrl) {
    const baseUrl = imageBaseUrl.replace(/\/$/, "");
    const imagePath = banner.photo.replace(/^\//, "");
    return `${baseUrl}/${imagePath}`;
  }

  // Fallback
  return "/images/banner/banner-first.webp";
};

// Helper function untuk mendapatkan collection logo URL
const getCollectionLogoUrl = (collection, imageBaseUrl) => {
  if (!collection?.logo)
    return "/images/collection/collection-placeholder.webp";

  // Jika sudah full URL
  if (
    collection.logo.startsWith("http://") ||
    collection.logo.startsWith("https://")
  ) {
    return collection.logo;
  }

  // Jika path relatif dari API
  if (imageBaseUrl) {
    const baseUrl = imageBaseUrl.replace(/\/$/, "");
    const imagePath = collection.logo.replace(/^\//, "");
    return `${baseUrl}/${imagePath}`;
  }

  // Fallback berdasarkan nama collection
  const name = collection.name?.toLowerCase() || "";

  return "/images/collection/collection-placeholder.webp";
};

// Helper function untuk mendapatkan category icon
const getCategoryIcon = (categoryName) => {
  if (!categoryName) return null;

  const name = categoryName.toLowerCase();

  // Mapping berdasarkan keyword dalam nama kategori
  if (name.includes("kacamata") || name.includes("glasses")) return "👓";
  if (name.includes("tas") || name.includes("bag")) return "👜";
  if (
    name.includes("baju") ||
    name.includes("kaos") ||
    name.includes("shirt") ||
    name.includes("dress")
  )
    return "👕";
  if (name.includes("aksesoris") || name.includes("accessories")) return "🎁";

  // Default icon
  return "📦";
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

/**
 * DynamicBanner - Wrapper component untuk banner/hero section
 */
export function DynamicBanner({
  banners: providedBanners = null,
  loading: providedLoading = false,
  error: providedError = null,
  imageBaseUrl: providedImageBaseUrl = "",
  ...props
}) {
  const { configData } = useConfig();

  // Fetch banners jika tidak disediakan
  const {
    banners: fetchedBanners,
    loading: fetchedLoading,
    error: fetchedError,
  } = useBanners({
    params: {
      status: "Active",
    },
  });

  // Gunakan provided atau fetched data
  const banners = providedBanners ?? fetchedBanners;
  const loading = providedLoading || fetchedLoading;
  const error = providedError || fetchedError;

  // Get sorted components dari config
  const sortedComponents = useMemo(() => {
    if (!configData?.ui_components) return null;
    return getSortedComponents(configData.ui_components);
  }, [configData]);

  // Get banner component dari config
  const bannerComponent = useMemo(() => {
    if (!sortedComponents) return null;
    return sortedComponents.find((c) => c.component_type === "banner");
  }, [sortedComponents]);

  // Get imageBaseUrl dari config jika tidak disediakan
  const imageBaseUrl =
    providedImageBaseUrl || configData?.config?.api?.imageUrl || "";

  // Transform banners ke format hero slides untuk fallback
  const heroSlides = useMemo(() => {
    if (!banners || banners.length === 0) {
      return [];
    }

    return banners
      .map((banner) => ({
        title: <>{banner.name || ""}</>,
        hook: banner.short_description || "",
        desc: banner.description || "",
        image: getBannerImageUrl(banner, imageBaseUrl),
        link: banner.link || "/collection",
      }))
      .filter((banner) => Boolean(banner.image));
  }, [banners, imageBaseUrl]);

  const sliderRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imageErrors, setImageErrors] = useState({});

  const heroSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 5000,
    fade: true,
    pauseOnHover: true,
  };

  const handlePrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const handleNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  // Render dynamic component jika ada config
  if (bannerComponent) {
    return (
      <DynamicComponent
        componentConfig={bannerComponent}
        banners={banners}
        loading={loading}
        error={error}
        imageBaseUrl={imageBaseUrl}
        {...props}
      />
    );
  }

  // Fallback ke hardcoded hero slider
  return (
    <section className="relative w-full">
      {loading ? (
        <div className="relative w-full h-[210px] md:h-[600px] lg:h-[600px] bg-nevada-200 animate-pulse flex items-center justify-center">
          <div className="text-nevada-500">Memuat banner...</div>
        </div>
      ) : error ? (
        <div className="relative w-full h-[210px] md:h-[600px] lg:h-[600px] bg-nevada-100 flex items-center justify-center">
          <div className="text-red-500 text-center px-4">
            Gagal memuat banner: {error}
          </div>
        </div>
      ) : heroSlides.length > 0 ? (
        <>
          <Slider
            {...heroSettings}
            className="w-full h-full"
            ref={sliderRef}
            afterChange={(index) => setCurrentSlide(index)}
          >
            {heroSlides.map((slide, idx) => (
              <Link
                key={idx}
                href={slide.link || "/collection"}
                prefetch={false}
                className="relative w-full h-[210px] md:h-[600px] lg:h-[600px]"
              >
                <Image
                  src={
                    imageErrors[idx]
                      ? "/images/banner/banner-first.webp"
                      : slide.image
                  }
                  alt={`Banner ${idx + 1}`}
                  fill
                  priority={idx === 0}
                  quality={100}
                  className="object-cover lg:object-cover aspect-video object-center w-full h-full z-0"
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 0,
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                  sizes="100vw"
                  onError={() =>
                    setImageErrors((prev) => ({ ...prev, [idx]: true }))
                  }
                />
              </Link>
            ))}
          </Slider>
          <div className="absolute left-2 md:left-12 bottom-2 sm:bottom-4 z-30 flex gap-2">
            <button
              onClick={handlePrev}
              aria-label="Sebelumnya"
              className="bg-white hover:bg-nevada-950 hover:text-white text-black rounded-full p-2 shadow transition"
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                <path
                  d="M13 15l-5-5 5-5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              onClick={handleNext}
              aria-label="Selanjutnya"
              className="bg-white hover:bg-nevada-950 hover:text-white text-black rounded-full p-2 shadow transition"
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                <path
                  d="M7 5l5 5-5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {heroSlides.length > 0 && (
              <div className="flex items-center ml-2 gap-1">
                {heroSlides.map((_, idx) => (
                  <span
                    key={idx}
                    className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${
                      currentSlide === idx ? "bg-nevada-950" : "bg-nevada-100"
                    } border border-white`}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}

/**
 * DynamicCategory - Wrapper component untuk category section
 * @param {boolean} [fullCategoryList=false] — Jika true (mis. halaman /category), tampilkan semua kategori tanpa items_limit.
 */
export function DynamicCategory({
  categories: providedCategories = null,
  loading: providedLoading = false,
  error: providedError = null,
  fullCategoryList = false,
  ...props
}) {
  const { configData } = useConfig();

  // Fetch categories jika tidak disediakan
  const {
    categories: fetchedCategories,
    loading: fetchedLoading,
    error: fetchedError,
  } = useProductCategories();

  // Gunakan provided atau fetched data
  const categories = providedCategories ?? fetchedCategories;
  const loading = providedLoading || fetchedLoading;
  const error = providedError || fetchedError;

  // Get sorted components dari config
  const sortedComponents = useMemo(() => {
    if (!configData?.ui_components) return null;
    return getSortedComponents(configData.ui_components);
  }, [configData]);

  // Get category component dari config
  const categoryComponent = useMemo(() => {
    if (!sortedComponents) return null;
    return sortedComponents.find((c) => c.component_type === "category");
  }, [sortedComponents]);

  // Render dynamic component jika ada config
  if (categoryComponent) {
    return (
      <DynamicComponent
        componentConfig={categoryComponent}
        categories={categories}
        loading={loading}
        error={error}
        fullCategoryList={fullCategoryList}
        {...props}
      />
    );
  }

  // Fallback ke hardcoded category grid
  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl sm:text-2xl font-semibold">
          Berdasarkan Kategori
        </h2>
        <Link
          href="/category"
          className="text-nevada-600 flex items-center gap-2 text-sm sm:text-base hover:text-nevada-950"
        >
          Lihat Lengkap <RiArrowRightSLine className="w-4 h-4" />
        </Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {[...Array(4)].map((_, idx) => (
            <div
              key={idx}
              className="bg-nevada-50 p-3 sm:p-4 rounded-2xl border border-nevada-100 animate-pulse"
            >
              <div className="flex items-center gap-3 sm:gap-6">
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-nevada-200 rounded"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-nevada-200 rounded w-3/4"></div>
                  <div className="h-3 bg-nevada-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-700 text-sm">Gagal memuat kategori: {error}</p>
        </div>
      ) : categories && categories.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {(fullCategoryList ? categories : categories.slice(0, 4)).map(
            (category) => {
              const Icon = getCategoryIcon(category?.name);
              const categoryName = category?.name || "Kategori";
              const productCount =
                category?.products_count || category?.products?.length || 0;

              return (
                <Link
                  key={category.id || category.slug}
                  href={`/collection?category=${
                    category?.id || category?.slug || ""
                  }`}
                  className="bg-nevada-50 p-3 sm:p-4 rounded-2xl border border-nevada-100 hover:border-nevada-950 transition"
                >
                  <div className="flex items-center gap-3 sm:gap-6">
                    {Icon && (
                      <span className="text-2xl sm:text-3xl">{Icon}</span>
                    )}
                    <div>
                      <h1 className="text-base sm:text-lg font-semibold text-nevada-950">
                        {categoryName}
                      </h1>
                      <div className="text-xs sm:text-sm text-nevada-600">
                        {productCount > 0
                          ? `${productCount} Produk`
                          : "Lihat Produk"}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            }
          )}
        </div>
      ) : (
        <div className="bg-nevada-50 border border-nevada-200 rounded-lg p-4 text-center">
          <p className="text-nevada-600 text-sm">Belum ada kategori tersedia</p>
        </div>
      )}
    </div>
  );
}

/**
 * DynamicProductList - Wrapper component untuk product list dengan tabs collections
 */
export function DynamicProductList({
  products: providedProducts = null,
  collections: providedCollections = null,
  loading: providedLoading = false,
  error: providedError = null,
  imageBaseUrl: providedImageBaseUrl = "",
  ...props
}) {
  const { configData } = useConfig();

  // Fetch collections jika tidak disediakan
  const {
    collections: fetchedCollections,
    loading: collectionsLoading,
    error: collectionsError,
  } = useCollections();

  // Gunakan provided atau fetched collections
  const collections = providedCollections ?? fetchedCollections;

  // State untuk track tab yang aktif
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  // Ambil collection yang aktif berdasarkan selectedTabIndex
  const activeCollection = useMemo(() => {
    if (!collections || collections.length === 0) return null;
    if (selectedTabIndex === 0) return null;
    return collections[selectedTabIndex - 1] || null;
  }, [collections, selectedTabIndex]);

  // Fetch products berdasarkan collection yang aktif
  const {
    products: allProducts,
    loading: allProductsLoading,
    error: allProductsError,
  } = useProducts({
    params: {
      per_page: 8,
    },
  });

  const {
    products: collectionProducts,
    loading: collectionProductsLoading,
    error: collectionProductsError,
  } = useProducts({
    params: {
      ...(activeCollection?.id && { collection_id: activeCollection.id }),
      per_page: 8,
    },
    enabled: selectedTabIndex > 0 && !!activeCollection?.id,
  });

  // Produk yang ditampilkan berdasarkan tab aktif
  const displayedProducts = useMemo(() => {
    if (selectedTabIndex === 0) {
      return providedProducts ?? allProducts ?? [];
    } else {
      return providedProducts ?? collectionProducts ?? [];
    }
  }, [selectedTabIndex, allProducts, collectionProducts, providedProducts]);

  const displayedProductsLoading =
    providedLoading ||
    (selectedTabIndex === 0 ? allProductsLoading : collectionProductsLoading);
  const displayedProductsError =
    providedError ||
    (selectedTabIndex === 0 ? allProductsError : collectionProductsError);

  // Get sorted components dari config
  const sortedComponents = useMemo(() => {
    if (!configData?.ui_components) return null;
    return getSortedComponents(configData.ui_components);
  }, [configData]);

  // Get product-list atau product-category component dari config
  const productListComponent = useMemo(() => {
    if (!sortedComponents) return null;
    return sortedComponents.find(
      (c) =>
        c.component_type === "product-list" ||
        c.component_type === "product-category"
    );
  }, [sortedComponents]);

  // Get imageBaseUrl dari config jika tidak disediakan
  const imageBaseUrl =
    providedImageBaseUrl || configData?.config?.api?.imageUrl || "";

  // Render dynamic component jika ada config
  if (productListComponent) {
    return (
      <DynamicComponent
        componentConfig={productListComponent}
        products={displayedProducts}
        loading={displayedProductsLoading}
        error={displayedProductsError}
        imageBaseUrl={imageBaseUrl}
        collections={collections}
        selectedTabIndex={selectedTabIndex}
        setSelectedTabIndex={setSelectedTabIndex}
        {...props}
      />
    );
  }

  // Fallback ke hardcoded product list dengan tabs
  return (
    <div className="space-y-4 sm:space-y-6">
      <h2 className="text-xl sm:text-2xl font-semibold">Koleksi Terbaru</h2>
      {collectionsLoading ? (
        <div className="flex flex-wrap space-x-0 sm:space-x-2 rounded-xl p-1 sm:p-2 w-full sm:w-max">
          {[...Array(3)].map((_, idx) => (
            <div
              key={idx}
              className="px-3 py-2 text-xs sm:px-4 sm:py-2 sm:text-sm bg-nevada-200 animate-pulse rounded w-24 sm:w-32 h-8 sm:h-10"
            ></div>
          ))}
        </div>
      ) : collectionsError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-700 text-sm">
            Gagal memuat koleksi: {collectionsError}
          </p>
        </div>
      ) : (
        <Tab.Group
          selectedIndex={selectedTabIndex}
          onChange={setSelectedTabIndex}
        >
          <Tab.List className="flex flex-wrap space-x-0 sm:space-x-2 rounded-xl p-1 sm:p-2 w-full sm:w-max">
            <Tab
              className={({ selected }) =>
                classNames(
                  "px-3 py-2 text-xs sm:px-4 sm:py-2 sm:text-sm focus:outline-none",
                  selected
                    ? "border-nevada-950 text-nevada-950 border-b-4 font-semibold"
                    : "text-nevada-600 hover:border-nevada-100 border-b font-normal"
                )
              }
            >
              Semua Koleksi
            </Tab>
            {collections && collections.length > 0
              ? collections.map((collection) => (
                  <Tab
                    key={collection.id || collection.slug}
                    className={({ selected }) =>
                      classNames(
                        "px-3 py-2 text-xs sm:px-4 sm:py-2 sm:text-sm focus:outline-none",
                        selected
                          ? "border-nevada-950 text-nevada-950 border-b-4 font-semibold"
                          : "text-nevada-600 hover:border-nevada-100 border-b font-normal"
                      )
                    }
                  >
                    {collection.name || "Koleksi"}
                  </Tab>
                ))
              : null}
          </Tab.List>
          <Tab.Panels className="mt-4 sm:mt-6">
            <Tab.Panel>
              {displayedProductsLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                  {[...Array(4)].map((_, idx) => (
                    <div
                      key={idx}
                      className="bg-white rounded-xl border border-nevada-100 animate-pulse"
                    >
                      <div className="w-full h-[200px] sm:h-[220px] md:h-[270px] bg-nevada-200"></div>
                      <div className="p-4 space-y-3">
                        <div className="h-4 bg-nevada-200 rounded w-1/4"></div>
                        <div className="h-5 bg-nevada-200 rounded w-3/4"></div>
                        <div className="h-4 bg-nevada-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : displayedProductsError ? (
                <div className="col-span-4 text-center text-red-500 py-8">
                  Error: {displayedProductsError}
                </div>
              ) : displayedProducts && displayedProducts.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                  {displayedProducts.map((product) => {
                    const productImage = buildProductImageUrl(
                      product.thumbnail ||
                        product.image ||
                        product.image_url ||
                        "",
                      imageBaseUrl
                    );
                    const productName =
                      product.name || product.title || "Produk";
                    const productCategory = getProductCategory(product);
                    const productPrice = getProductPrice(product);
                    const productDiscountPrice =
                      getProductDiscountPrice(product);
                    const productRating = getProductRating(product);
                    const productId = product.id || product.slug;

                    return (
                      <div
                        key={productId}
                        className="bg-white rounded-xl border border-nevada-100 hover:border-nevada-950 transition overflow-hidden flex flex-col"
                      >
                        <div className="flex justify-center items-center w-full bg-white relative group">
                          {isProductPreorder(product) && (
                            <span className="absolute top-3 left-3 z-20 bg-nevada-700 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full">
                              Preorder
                            </span>
                          )}
                          <SafeImage
                            src={productImage}
                            fallback="/images/product/product-placeholder.webp"
                            alt={productName}
                            width={368}
                            height={270}
                            className="object-scale-down w-full h-[200px] sm:h-[220px] md:h-[270px]"
                          />
                          <div className="absolute -mb-20 left-1/2 -translate-x-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto">
                            <div className="flex gap-2">
                              <Link
                                href="/wishlist"
                                prefetch={false}
                                className="bg-white rounded-lg hover:border-nevada-950 p-1.5 border border-nevada-100 pointer-events-auto"
                              >
                                <RiHeart3Line className="text-nevada-950 w-6 h-6" />
                              </Link>
                              <Link
                                href={getProductDetailHref(product) || "#"}
                                prefetch={false}
                                className="bg-white rounded-lg hover:border-nevada-950 p-1.5 border border-nevada-100 pointer-events-auto"
                              >
                                <RiEyeLine className="text-nevada-950 w-6 h-6" />
                              </Link>
                              <Link
                                href="/cart"
                                prefetch={false}
                                className="bg-white rounded-lg hover:border-nevada-950 p-1.5 border border-nevada-100 pointer-events-auto"
                              >
                                <RiShoppingBag3Line className="text-nevada-950 w-6 h-6" />
                              </Link>
                            </div>
                          </div>
                        </div>
                        <div className="p-4 flex flex-col flex-1 space-y-3">
                          {productCategory ? (
                            <div className="text-sm text-nevada-500 mb-1">
                              {productCategory}
                            </div>
                          ) : null}
                          <Link
                            href={getProductDetailHref(product) || "#"}
                            className="font-semibold text-lg text-nevada-950 mb-1 line-clamp-2"
                          >
                            {productName}
                          </Link>
                          <div className={`${productRating > 0 ? "flex" : "hidden"} items-center gap-1 text-yellow-400 text-sm mb-2`}>
                            {Array.from({ length: 5 }).map((_, i) => (
                              <svg
                                key={i}
                                className={
                                  i < Math.round(productRating)
                                    ? "fill-yellow-400"
                                    : "fill-nevada-200"
                                }
                                width="16"
                                height="16"
                                viewBox="0 0 20 20"
                              >
                                <polygon points="10,1 12.59,7.36 19.51,7.64 14,12.14 15.82,19.02 10,15.27 4.18,19.02 6,12.14 0.49,7.64 7.41,7.36" />
                              </svg>
                            ))}
                            <span className="text-nevada-500 ml-1">
                              {productRating.toFixed(1)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-auto">
                            <span className="font-bold text-lg text-nevada-950">
                              Rp{formatRupiah(productDiscountPrice)}
                            </span>
                            {productDiscountPrice < productPrice && (
                              <span className="text-sm text-red-700 line-through">
                                Rp{formatRupiah(productPrice)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="col-span-4 text-center text-nevada-500 py-8">
                  Belum ada produk tersedia.
                </div>
              )}
            </Tab.Panel>
            {collections && collections.length > 0
              ? collections.map((collection) => (
                  <Tab.Panel key={collection.id || collection.slug}>
                    {displayedProductsLoading ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                        {[...Array(4)].map((_, idx) => (
                          <div
                            key={idx}
                            className="bg-white rounded-xl border border-nevada-100 animate-pulse"
                          >
                            <div className="w-full h-[200px] sm:h-[220px] md:h-[270px] bg-nevada-200"></div>
                            <div className="p-4 space-y-3">
                              <div className="h-4 bg-nevada-200 rounded w-1/4"></div>
                              <div className="h-5 bg-nevada-200 rounded w-3/4"></div>
                              <div className="h-4 bg-nevada-200 rounded w-1/2"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : displayedProductsError ? (
                      <div className="col-span-4 text-center text-red-500 py-8">
                        Error: {displayedProductsError}
                      </div>
                    ) : displayedProducts && displayedProducts.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                        {displayedProducts.map((product) => {
                          const productImage = buildProductImageUrl(
                            product.thumbnail ||
                              product.image ||
                              product.image_url ||
                              "",
                            imageBaseUrl
                          );
                          const productName =
                            product.name || product.title || "Produk";
                          const productCategory = getProductCategory(product);
                          const productPrice = getProductPrice(product);
                          const productDiscountPrice =
                            getProductDiscountPrice(product);
                          const productRating = getProductRating(product);
                          const productId = product.id || product.slug;

                          return (
                            <div
                              key={productId}
                              className="bg-white rounded-xl border border-nevada-100 hover:border-nevada-950 transition overflow-hidden flex flex-col"
                            >
                              <div className="flex justify-center items-center w-full bg-white relative group">
                                {isProductPreorder(product) && (
                                  <span className="absolute top-3 left-3 z-20 bg-nevada-700 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full">
                                    Preorder
                                  </span>
                                )}
                                <SafeImage
                                  src={productImage}
                                  fallback="/images/product/product-placeholder.webp"
                                  alt={productName}
                                  width={368}
                                  height={270}
                                  className="object-scale-down w-full h-[200px] sm:h-[220px] md:h-[270px]"
                                />
                                <div className="absolute -mb-20 left-1/2 -translate-x-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto">
                                  <div className="flex gap-2">
                                    <Link
                                      href="/wishlist"
                                      prefetch={false}
                                      className="bg-white rounded-lg hover:border-nevada-950 p-1.5 border border-nevada-100 pointer-events-auto"
                                    >
                                      <RiHeart3Line className="text-nevada-950 w-6 h-6" />
                                    </Link>
                                    <Link
                                      href={
                                        getProductDetailHref(product) || "#"
                                      }
                                      prefetch={false}
                                      className="bg-white rounded-lg hover:border-nevada-950 p-1.5 border border-nevada-100 pointer-events-auto"
                                    >
                                      <RiEyeLine className="text-nevada-950 w-6 h-6" />
                                    </Link>
                                    <Link
                                      href="/cart"
                                      prefetch={false}
                                      className="bg-white rounded-lg hover:border-nevada-950 p-1.5 border border-nevada-100 pointer-events-auto"
                                    >
                                      <RiShoppingBag3Line className="text-nevada-950 w-6 h-6" />
                                    </Link>
                                  </div>
                                </div>
                              </div>
                              <div className="p-4 flex flex-col flex-1 space-y-3">
                                {productCategory ? (
                                  <div className="text-sm text-nevada-500 mb-1">
                                    {productCategory}
                                  </div>
                                ) : null}
                                <Link
                                  href={getProductDetailHref(product) || "#"}
                                  className="font-semibold text-lg text-nevada-950 mb-1 line-clamp-2"
                                >
                                  {productName}
                                </Link>
                                <div className={`${productRating > 0 ? "flex" : "hidden"} items-center gap-1 text-yellow-400 text-sm mb-2`}>
                                  {Array.from({ length: 5 }).map((_, i) => (
                                    <svg
                                      key={i}
                                      className={
                                        i < Math.round(productRating)
                                          ? "fill-yellow-400"
                                          : "fill-nevada-200"
                                      }
                                      width="16"
                                      height="16"
                                      viewBox="0 0 20 20"
                                    >
                                      <polygon points="10,1 12.59,7.36 19.51,7.64 14,12.14 15.82,19.02 10,15.27 4.18,19.02 6,12.14 0.49,7.64 7.41,7.36" />
                                    </svg>
                                  ))}
                                  <span className="text-nevada-500 ml-1">
                                    {productRating.toFixed(1)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mt-auto">
                                  <span className="font-bold text-lg text-nevada-950">
                                    Rp{formatRupiah(productDiscountPrice)}
                                  </span>
                                  {productDiscountPrice < productPrice && (
                                    <span className="text-sm text-red-700 line-through">
                                      Rp{formatRupiah(productPrice)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="col-span-4 text-center text-nevada-500 py-8">
                        Tidak ada produk tersedia untuk koleksi ini.
                      </div>
                    )}
                  </Tab.Panel>
                ))
              : null}
          </Tab.Panels>
        </Tab.Group>
      )}
    </div>
  );
}

/**
 * DynamicCollection - Wrapper component untuk collection showcase (2 cards)
 */
export function DynamicCollection({
  collections: providedCollections = null,
  loading: providedLoading = false,
  error: providedError = null,
  imageBaseUrl: providedImageBaseUrl = "",
  ...props
}) {
  const { configData } = useConfig();

  // Fetch collections jika tidak disediakan
  const {
    collections: fetchedCollections,
    loading: fetchedLoading,
    error: fetchedError,
  } = useCollections();

  // Gunakan provided atau fetched data
  const collections = providedCollections ?? fetchedCollections;
  const loading = providedLoading || fetchedLoading;
  const error = providedError || fetchedError;

  // Get imageBaseUrl dari config jika tidak disediakan
  const imageBaseUrl =
    providedImageBaseUrl || configData?.config?.api?.imageUrl || "";

  // Transform collections ke format section koleksi
  const koleksiItems = useMemo(() => {
    // Jika tidak ada koleksi, jangan tampilkan data dummy.
    // Biarkan array kosong supaya fallback "Belum ada koleksi tersedia" yang muncul.
    if (!collections || collections.length === 0) {
      return [];
    }

    return collections.slice(0, 2).map((collection) => ({
      title: "Koleksi",
      subtitle: collection.name || "Koleksi",
      desc: collection.short_description || collection.description || "",
      image: getCollectionLogoUrl(collection, imageBaseUrl),
      href: collection.product_id
        ? `/shop/${collection.product_id}`
        : "/collection",
    }));
  }, [collections, imageBaseUrl]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
          {[...Array(2)].map((_, idx) => (
            <div
              key={idx}
              className="bg-nevada-50 relative space-y-2 sm:space-y-4 px-4 sm:px-8 py-6 sm:py-12 rounded-2xl border border-nevada-100 animate-pulse"
            >
              <div className="flex flex-col sm:flex-row justify-between">
                <div className="space-y-3 sm:space-y-6 flex-1">
                  <div className="h-5 bg-nevada-200 rounded w-20"></div>
                  <div className="h-8 bg-nevada-200 rounded w-32"></div>
                  <div className="h-4 bg-nevada-200 rounded w-40"></div>
                  <div className="h-10 bg-nevada-200 rounded w-36"></div>
                </div>
                <div className="w-24 h-36 bg-nevada-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-700 text-sm">Gagal memuat koleksi: {error}</p>
        </div>
      ) : koleksiItems && koleksiItems.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
          {koleksiItems.map((item, idx) => (
            <Link
              key={idx}
              href={item.href}
              className="bg-nevada-50 relative space-y-2 sm:space-y-4 hover:border-nevada-950 px-4 sm:px-8 py-2 sm:py-12 rounded-2xl border border-nevada-100"
            >
              <div className="flex md:flex-col items-center md:items-stretch flex-row justify-between">
                <div className="space-y-3 sm:space-y-6">
                  <h2 className="text-base sm:text-lg font-semibold">
                    {item.title}
                  </h2>
                  <div className="text-2xl sm:text-4xl font-semibold">
                    {item.subtitle}
                  </div>
                  <div className="text-nevada-600 text-sm sm:text-base">
                    {item.desc}
                  </div>

                  <div className="bg-nevada-600 hover:bg-nevada-950 hover:text-white text-white w-max font-semibold rounded-lg py-2 px-4 sm:py-3 sm:px-6 text-sm sm:text-base">
                    Belanja Sekarang
                  </div>
                </div>
                <div className="absolute right-2 -mt-6 hidden sm:block w-[190px] h-[285px]">
                  <SafeImage
                    src={item.image}
                    fallback="/images/collection/collection-placeholder.webp"
                    alt={item.subtitle}
                    width={190}
                    height={285}
                    className="w-full h-full object-contain"
                    sizes="190px"
                  />
                </div>
                <div className="flex flex-wrap justify-end w-[150px] h-[150px] sm:hidden mt-4">
                  <SafeImage
                    src={item.image}
                    fallback="/images/collection/collection-placeholder.webp"
                    alt={item.subtitle}
                    width={150}
                    height={150}
                    className="w-full h-full object-contain"
                    sizes="150px"
                  />
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="bg-nevada-50 border border-nevada-200 rounded-lg p-4 text-center">
          <p className="text-nevada-600 text-sm">Belum ada koleksi tersedia</p>
        </div>
      )}
    </div>
  );
}

/**
 * DynamicBlog - Wrapper component untuk blog section
 */
export function DynamicBlog({
  blogs: providedBlogs = null,
  loading: providedLoading = false,
  error: providedError = null,
  componentConfig: providedComponentConfig = null,
  imageBaseUrl: providedImageBaseUrl = "",
  ...props
}) {
  const { configData } = useConfig();
  const teamId = configData?.team_id;
  const imageBaseUrl =
    providedImageBaseUrl || configData?.config?.api?.imageUrl || "";

  // Get sorted components dari config
  const sortedComponents = useMemo(() => {
    if (!configData?.ui_components) return null;
    return getSortedComponents(configData.ui_components);
  }, [configData]);

  // Get blog component dari config
  const blogComponent = useMemo(() => {
    if (providedComponentConfig) return providedComponentConfig;
    if (!sortedComponents) return null;
    return sortedComponents.find((c) => c.component_type === "blog");
  }, [providedComponentConfig, sortedComponents]);

  // State untuk blog
  const [blogs, setBlogs] = useState(providedBlogs || []);
  const [blogsLoading, setBlogsLoading] = useState(providedLoading);
  const [blogsError, setBlogsError] = useState(providedError);

  // Fetch blog data jika tidak disediakan
  useEffect(() => {
    if (providedBlogs !== null) {
      setBlogs(providedBlogs);
      setBlogsLoading(providedLoading);
      setBlogsError(providedError);
      return;
    }

    if (!teamId) {
      return;
    }

    let isMounted = true;

    const fetchBlogs = async () => {
      try {
        setBlogsLoading(true);
        setBlogsError(null);

        const res = await fetch(`/api/blog?team_id=${teamId}`);
        if (!res.ok) {
          throw new Error("Gagal memuat blog");
        }

        const data = await res.json();

        let items = [];

        if (Array.isArray(data)) {
          items = data;
        } else if (Array.isArray(data?.data)) {
          items = data.data;
        } else if (Array.isArray(data?.data?.data)) {
          items = data.data.data;
        } else if (Array.isArray(data?.blogs)) {
          items = data.blogs;
        }

        if (!isMounted) return;
        setBlogs(items);
      } catch (error) {
        if (!isMounted) return;
        setBlogsError(error?.message || "Terjadi kesalahan saat memuat blog");
      } finally {
        if (!isMounted) return;
        setBlogsLoading(false);
      }
    };

    fetchBlogs();

    return () => {
      isMounted = false;
    };
  }, [teamId, providedBlogs, providedLoading, providedError]);

  if (blogComponent) {
    if (!blogsLoading && (blogsError || blogs.length === 0)) {
      return null;
    }
    return (
      <DynamicComponent
        componentConfig={blogComponent}
        blogs={blogs}
        loading={blogsLoading}
        error={blogsError}
        imageBaseUrl={imageBaseUrl}
        {...props}
      />
    );
  }

  if (!blogsLoading && (blogsError || blogs.length === 0)) {
    return null;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex w-full justify-between items-center">
        <div>
          <div className="font-semibold text-nevada-700 text-base sm:text-lg">
            Postingan terbaru
          </div>
          <h2 className="text-xl sm:text-2xl font-semibold">
            Tips dan Trik Menarik!
          </h2>
        </div>
        <Link
          href="/blog"
          className="text-nevada-600 text-sm sm:text-base flex items-center gap-2"
        >
          Lihat Semua <RiArrowRightSLine />
        </Link>
      </div>
      {blogsLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {[...Array(4)].map((_, idx) => (
            <div
              key={idx}
              className="bg-white rounded-2xl border border-nevada-100 animate-pulse"
            >
              <div className="w-full h-52 sm:h-48 bg-nevada-200 rounded-t-2xl" />
              <div className="p-4 sm:p-6 space-y-3">
                <div className="h-3 bg-nevada-200 rounded w-1/2" />
                <div className="h-4 bg-nevada-200 rounded w-3/4" />
                <div className="h-3 bg-nevada-200 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : blogsError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-700 text-sm">
            Gagal memuat blog: {blogsError}
          </p>
        </div>
      ) : blogs && blogs.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {blogs.map((post, idx) => {
            const imageSrc =
              post?.image ||
              post?.thumbnail ||
              (Array.isArray(post?.images) ? post.images[0] : post?.images) ||
              "/images/article/article-1.webp";

            const category =
              post?.category?.name ||
              post?.categories?.[0]?.name ||
              post?.tags?.[0]?.name ||
              post?.category ||
              post?.tag ||
              "Blog";

            let dateText = "";
            const rawDate =
              post?.published_at || post?.created_at || post?.date || "";
            if (rawDate) {
              const d = new Date(rawDate);
              if (!isNaN(d.getTime())) {
                dateText = d.toLocaleDateString("id-ID", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                });
              } else {
                dateText = rawDate;
              }
            }

            const title = post?.title || post?.name || "Judul artikel menarik";

            const rawContent =
              post?.excerpt ||
              post?.summary ||
              post?.description ||
              post?.content ||
              "";
            const plainContent =
              typeof rawContent === "string"
                ? rawContent
                    .replace(/<[^>]+>/g, " ")
                    .replace(/\s+/g, " ")
                    .trim()
                : "";
            const excerpt =
              plainContent && plainContent.length > 140
                ? `${plainContent.slice(0, 140)}...`
                : plainContent ||
                  "Baca selengkapnya untuk mendapatkan tips dan informasi menarik.";

            return (
              <Link
                key={post?.id || post?.slug || idx}
                href={`/blog/${post?.slug}`}
                className="bg-white rounded-2xl border border-nevada-100 hover:border-nevada-950 flex flex-row sm:flex-col"
              >
                <div className="w-32 h-32 sm:w-full sm:h-48 relative rounded-l-2xl sm:rounded-l-none sm:rounded-t-2xl overflow-hidden flex-shrink-0">
                  <Image
                    src={imageSrc}
                    alt={title}
                    fill
                    className="object-cover w-full h-full"
                    sizes="(max-width: 768px) 128px, 25vw"
                  />
                </div>
                <div className="p-4 sm:p-6 space-y-2 sm:space-y-4 flex-1">
                  <div className="flex text-xs sm:text-base">
                    <b>{category}</b>
                    {dateText && <span className="ml-1">- {dateText}</span>}
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold line-clamp-2">
                    {title}
                  </h3>
                  <div className="text-gray-600 dark:text-gray-300 text-xs sm:text-base line-clamp-2 sm:line-clamp-3">
                    {excerpt}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="bg-nevada-50 border border-nevada-200 rounded-lg p-4 text-center">
          <p className="text-nevada-600 text-sm">
            Belum ada postingan blog tersedia.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * DynamicTestimonial - Wrapper component untuk testimonial section
 */
export function DynamicTestimonial({
  testimonials: providedTestimonials = null,
  loading: providedLoading = false,
  error: providedError = null,
  componentConfig: providedComponentConfig = null,
  imageBaseUrl: providedImageBaseUrl = "",
  ...props
}) {
  const { configData } = useConfig();
  const imageBaseUrl =
    providedImageBaseUrl || configData?.config?.api?.imageUrl || "";

  // Get sorted components dari config
  const sortedComponents = useMemo(() => {
    if (!configData?.ui_components) return null;
    return getSortedComponents(configData.ui_components);
  }, [configData]);

  // Get testimonial component dari config
  const testimonialComponent = useMemo(() => {
    if (providedComponentConfig) return providedComponentConfig;
    if (!sortedComponents) return null;
    return sortedComponents.find((c) => c.component_type === "testimonial");
  }, [providedComponentConfig, sortedComponents]);

  // State untuk testimonial
  const [testimonials, setTestimonials] = useState(providedTestimonials || []);
  const [loading, setLoading] = useState(providedLoading);
  const [error, setError] = useState(providedError);

  // Fetch testimonial data jika tidak disediakan
  useEffect(() => {
    if (providedTestimonials !== null) {
      setTestimonials(providedTestimonials);
      setLoading(providedLoading);
      setError(providedError);
      return;
    }

    let isMounted = true;

    const fetchTestimonials = async () => {
      try {
        const teamId = configData?.team_id;
        if (!teamId) {
          setTestimonials([]);
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);

        const query = `?team_id=${encodeURIComponent(String(teamId))}`;
        const res = await fetch(`/api/estore/testimoni${query}`);
        if (!res.ok) {
          throw new Error("Gagal memuat testimoni");
        }

        const data = await res.json();

        let items = [];
        if (Array.isArray(data)) {
          items = data;
        } else if (Array.isArray(data?.data)) {
          items = data.data;
        } else if (Array.isArray(data?.data?.data)) {
          items = data.data.data;
        } else if (Array.isArray(data?.testimonials)) {
          items = data.testimonials;
        }

        if (!isMounted) return;
        setTestimonials(items);
      } catch (e) {
        if (!isMounted) return;
        setError(e?.message || "Terjadi kesalahan saat memuat testimoni");
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    fetchTestimonials();
    return () => {
      isMounted = false;
    };
  }, [
    configData?.team_id,
    providedTestimonials,
    providedLoading,
    providedError,
  ]);

  if (testimonialComponent) {
    if (!loading && (error || testimonials.length === 0)) {
      return null;
    }
    return (
      <DynamicComponent
        componentConfig={testimonialComponent}
        testimonials={testimonials}
        loading={loading}
        error={error}
        imageBaseUrl={imageBaseUrl}
        {...props}
      />
    );
  }

  // Jika tidak ada config testimonial, render tetap via DynamicComponent jika caller memberi config,
  // atau biarkan null agar halaman menentukan fallback.
  return null;
}

/**
 * DynamicCTA - Wrapper component untuk CTA section
 */
export function DynamicCTA({ ...props }) {
  const { configData } = useConfig();

  // Get sorted components dari config
  const sortedComponents = useMemo(() => {
    if (!configData?.ui_components) return null;
    return getSortedComponents(configData.ui_components);
  }, [configData]);

  // Get CTA component dari config
  const ctaComponent = useMemo(() => {
    if (!sortedComponents) return null;
    return sortedComponents.find((c) => c.component_type === "cta");
  }, [sortedComponents]);

  // Render dynamic component jika ada config
  if (ctaComponent) {
    return <DynamicComponent componentConfig={ctaComponent} {...props} />;
  }

  return null;
}
